import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Calendar, Clock, ArrowRight, BookOpen } from 'lucide-react';
import SEO from '../components/SEO';
import CTASection from '../components/CTASection';
import { resourcesData } from '../data/resourcesData';

export default function Resources({ onOpenConsultation }) {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  const categories = ['All', 'Mental Health', 'Addiction & Recovery', 'Family Guidance', 'Rehabilitation', 'Mental Wellness'];

  const filteredArticles = resourcesData.filter((article) => {
    const matchesCategory = selectedCategory === 'All' || article.category === selectedCategory;
    const matchesSearch = article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          article.shortDescription.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="bg-canvas">
      <SEO
        title="Resources & Insights | Mental Health & Deaddiction Articles"
        description="Educational articles, caregiver guides, and psychiatric insights by UMED's clinical experts. Learn about recovery, relapse prevention, and mental wellness."
      />

      {/* Hero */}
      <section className="relative pt-16 pb-20 lg:pt-24 lg:pb-28 bg-[#123B5D] text-white overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-[#0F766E]/30 via-[#123B5D] to-[#123B5D] pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 mb-4 px-3 py-1 rounded-full bg-[#0F766E] border border-[#2A9D8F]/30">
              <BookOpen className="w-3.5 h-3.5 text-[#2A9D8F]" />
              <span className="text-xs uppercase tracking-widest text-white font-semibold">
                Clinical Insights & Mental Wellness
              </span>
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white font-normal leading-tight">
              Knowledge That Empowers <span className="text-[#2A9D8F]">Healing & Hope</span>.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-[#D9E8E5] leading-relaxed">
              Explore scientific, compassionate guides on psychiatric conditions, caregiver wellness, cognitive rehabilitation, and addiction recovery written by our multidisciplinary specialists.
            </p>
          </div>
        </div>
      </section>

      {/* Filter & Search Bar */}
      <section className="py-12 border-b border-[#D9E8E5] bg-[#F5FAF9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            
            {/* Category Pills */}
            <div className="flex items-center gap-2 overflow-x-auto pb-2 md:pb-0 w-full md:w-auto no-scrollbar">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${
                    selectedCategory === cat
                      ? 'bg-[#123B5D] text-white shadow-sm'
                      : 'bg-white text-[#64748B] hover:bg-[#F5FAF9] border border-[#D9E8E5]'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            {/* Search Input */}
            <div className="relative w-full md:w-72">
              <Search className="w-4 h-4 text-[#64748B] absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="text"
                placeholder="Search articles & guides..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 rounded-full border border-[#D9E8E5] bg-white text-xs text-[#18323A] focus:outline-none focus:border-[#0F766E] focus:ring-1 focus:ring-[#0F766E]"
              />
            </div>

          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredArticles.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-3xl border border-[#D9E8E5] max-w-lg mx-auto">
              <BookOpen className="w-12 h-12 text-[#64748B] mx-auto mb-3" />
              <h3 className="font-serif text-xl text-[#123B5D] font-normal">No articles found</h3>
              <p className="text-xs text-[#64748B] mt-1">Try refining your search term or select another category.</p>
              <button
                onClick={() => { setSelectedCategory('All'); setSearchQuery(''); }}
                className="mt-4 text-xs font-semibold text-[#0F766E] underline"
              >
                Reset Filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredArticles.map((article) => (
                <div
                  key={article.id}
                  className="group bg-white rounded-2xl border border-[#D9E8E5] overflow-hidden shadow-card hover:shadow-elevated transition-all duration-300 flex flex-col justify-between hover:-translate-y-1"
                >
                  <div>
                    <div className="relative h-52 overflow-hidden bg-[#123B5D]">
                      <img
                        src={article.image}
                        alt={article.title}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      <div className="absolute top-4 left-4">
                        <span className="text-[11px] font-semibold px-3 py-1 rounded-full bg-[#123B5D]/80 text-white backdrop-blur-sm border border-white/20">
                          {article.category}
                        </span>
                      </div>
                    </div>

                    <div className="p-6">
                      <div className="flex items-center gap-4 text-xs text-[#64748B] mb-3">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5 text-[#0F766E]" />
                          {article.date}
                        </span>
                        <span>&bull;</span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5 text-[#0F766E]" />
                          {article.readTime}
                        </span>
                      </div>

                      <h3 className="font-serif text-xl text-[#123B5D] font-normal leading-snug group-hover:text-[#0F766E] transition-colors mb-3">
                        {article.title}
                      </h3>

                      <p className="text-xs text-[#64748B] leading-relaxed line-clamp-3">
                        {article.shortDescription}
                      </p>
                    </div>
                  </div>

                  <div className="p-6 pt-0 mt-auto border-t border-[#D9E8E5] pt-4 flex items-center justify-between">
                    <span className="text-[11px] font-medium text-[#64748B]">By {article.author}</span>
                    <Link
                      to={`/resources/${article.slug}`}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-[#0F766E] group-hover/link:text-[#123B5D]"
                    >
                      <span>Read Article</span>
                      <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <CTASection onOpenConsultation={onOpenConsultation} />
    </div>
  );
}
